import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Nfc, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { NumericKeypad } from "../components/NumericKeypad";

const MATRICULE_LENGTH = 7;
const PIN_LENGTH = 8;
// Un lecteur de badge USB (RFID comme NFC) se comporte comme un CLAVIER : il
// "tape" le code du badge très vite, suivi d'Entrée. On distingue ça d'une
// vraie frappe humaine par la VITESSE entre deux caractères — un humain qui
// tape sur un clavier physique dépasse rarement 1 caractère toutes les
// 30-40ms de façon soutenue sur toute une séquence, un lecteur badge est
// quasi instantané (souvent <10ms/caractère).
const DELAI_MAX_ENTRE_CARACTERES_MS = 40;
const LONGUEUR_MIN_BADGE = 4;

// Couleurs fixes de l'écran de connexion (indépendantes du thème clair/sombre
// du reste de l'application — gabarit demandé à l'identique).
const C = {
  bg: "#12151a",
  panel: "#1a1e25",
  panel2: "#20252d",
  line: "#2b313b",
  amber: "#e8a33d",
  text: "#f0ede4",
  muted: "#8b909b",
};

export default function Login() {
  const [step, setStep] = useState("matricule"); // "matricule" | "pin"
  const [matricule, setMatricule] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [nfcScanning, setNfcScanning] = useState(false);
  const { login, loginBadge, centre } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const length = step === "matricule" ? MATRICULE_LENGTH : PIN_LENGTH;
  const value = step === "matricule" ? matricule : pin;

  const nfcDisponible = typeof window !== "undefined" && "NDEFReader" in window;

  const submitBadge = async (code) => {
    if (!code || loading) return;
    setLoading(true);
    setError("");
    try {
      await loginBadge(code);
      navigate(location.state?.from || "/", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Badge non reconnu");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (fullPin) => {
    setLoading(true);
    setError("");
    try {
      await login(matricule, fullPin);
      navigate(location.state?.from || "/", { replace: true });
    } catch (e) {
      // Les messages du backend (tentatives restantes, durée de blocage) sont
      // déjà rédigés pour être affichés tels quels — voir routers/auth_routes.py.
      setError(formatApiError(e.response?.data?.detail) || "Identifiant ou code incorrect");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const press = (digit) => {
    if (loading) return;
    if (step === "matricule") {
      const next = (matricule + digit).slice(0, MATRICULE_LENGTH);
      setMatricule(next);
      setError("");
      if (next.length === MATRICULE_LENGTH) setStep("pin");
      return;
    }
    const next = (pin + digit).slice(0, PIN_LENGTH);
    setPin(next);
    if (next.length === PIN_LENGTH) submit(next);
  };

  const backspace = () => {
    if (step === "matricule") setMatricule((p) => p.slice(0, -1));
    else setPin((p) => p.slice(0, -1));
  };

  // Bouton "OK" / touche Entrée du pavé : n'agit que si la saisie en cours
  // est déjà complète (sinon rien ne se passe, exactement comme le clic sur
  // les chiffres suffit normalement à avancer tout seul — ce bouton sert de
  // confirmation explicite, pas d'action différente).
  const handleOk = () => {
    if (loading) return;
    if (step === "matricule" && matricule.length === MATRICULE_LENGTH) {
      setStep("pin");
    } else if (step === "pin" && pin.length === PIN_LENGTH) {
      submit(pin);
    }
  };

  // Écoute globale du clavier — capte un lecteur de badge USB, quel que soit
  // l'endroit où le focus se trouve sur la page (pas besoin d'un champ texte
  // dédié). Reste actif tant qu'on est sur l'écran de connexion, désactivé
  // pendant un chargement pour éviter un double envoi.
  //
  // Corrige un vrai bug : jusqu'ici, taper un chiffre au clavier physique à
  // vitesse humaine normale ne faisait RIEN à l'écran (le tampon de
  // détection de badge l'absorbait silencieusement, sans jamais l'envoyer
  // au pavé visible). Un chiffre isolé (pas suivi d'une rafale rapide) est
  // maintenant transmis au pavé après un très court délai de confirmation —
  // le temps de vérifier qu'aucun autre caractère ne suit immédiatement
  // derrière (auquel cas c'est un badge, pas une vraie frappe).
  const bufferRef = useRef("");
  const dernierCaractereRef = useRef(0);
  const frappeEnAttenteRef = useRef(null);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (loading) return;
      const maintenant = Date.now();

      if (e.key === "Backspace") {
        if (frappeEnAttenteRef.current) { clearTimeout(frappeEnAttenteRef.current); frappeEnAttenteRef.current = null; }
        backspace();
        return;
      }

      if (e.key === "Enter") {
        if (frappeEnAttenteRef.current) { clearTimeout(frappeEnAttenteRef.current); frappeEnAttenteRef.current = null; }
        const code = bufferRef.current;
        bufferRef.current = "";
        if (code.length >= LONGUEUR_MIN_BADGE) submitBadge(code);
        else handleOk();
        return;
      }

      if (e.key.length !== 1) return; // ignore Shift, Tab, flèches...

      const rapide = maintenant - dernierCaractereRef.current <= DELAI_MAX_ENTRE_CARACTERES_MS;
      if (!rapide) {
        bufferRef.current = ""; // trop lent pour être un lecteur badge : on repart de zéro
      } else if (frappeEnAttenteRef.current) {
        // Ce caractère arrive juste après le précédent : c'est bien une
        // rafale de badge, pas une frappe humaine isolée — on annule la
        // transmission au pavé qui avait été provisoirement programmée.
        clearTimeout(frappeEnAttenteRef.current);
        frappeEnAttenteRef.current = null;
      }
      bufferRef.current += e.key;
      dernierCaractereRef.current = maintenant;

      if (/^[0-9]$/.test(e.key)) {
        const digit = e.key;
        frappeEnAttenteRef.current = setTimeout(() => {
          frappeEnAttenteRef.current = null;
          press(digit);
        }, DELAI_MAX_ENTRE_CARACTERES_MS + 20);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, step, matricule, pin]);

  const scannerViaNfc = async () => {
    setNfcScanning(true);
    setError("");
    try {
      // eslint-disable-next-line no-undef
      const reader = new NDEFReader();
      await reader.scan();
      reader.onreading = (event) => {
        const code = event.serialNumber || "";
        if (code) submitBadge(code.toUpperCase());
        setNfcScanning(false);
      };
    } catch (err) {
      setError("Lecture NFC impossible sur cet appareil.");
      setNfcScanning(false);
    }
  };

  const backToMatricule = () => {
    setStep("matricule");
    setPin("");
    setError("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: C.bg, fontFamily: "'Space Grotesk', sans-serif", color: C.text }}
    >
      <div
        className="w-full max-w-sm rounded-[20px] px-8 py-9"
        style={{ background: C.panel, border: `1px solid ${C.line}`, boxShadow: "0 30px 60px -20px rgba(0,0,0,0.5)" }}
      >
        <p className="text-center text-xs tracking-wide mb-1" style={{ color: C.muted }}>
          SOGO FORMATION
        </p>
        <div className="text-center mb-5">
          <h1 className="text-[22px] font-semibold mb-1">Connexion</h1>
          <p className="text-sm" style={{ color: C.muted }}>{centre?.nom && centre.nom !== "Centre de formation" ? `${centre.nom} — ` : ""}Matricule puis code PIN</p>
        </div>

        {step === "pin" && (
          <button
            onClick={backToMatricule}
            disabled={loading}
            data-testid="login-back-to-matricule"
            className="flex items-center gap-1.5 text-sm mb-3 mx-auto hover:opacity-70 transition-opacity"
            style={{ color: C.muted }}
          >
            <ArrowLeft size={14} /> Matricule {matricule}
          </button>
        )}

        <p className="text-center text-xs font-medium mb-2" style={{ color: C.muted }} data-testid="login-role-label">
          {step === "matricule" ? `Matricule (${MATRICULE_LENGTH} chiffres)` : `Code PIN (${PIN_LENGTH} chiffres)`}
        </p>

        <div
          className="rounded-xl p-4 mb-5 flex items-center justify-center gap-2.5 flex-wrap"
          style={{ background: C.panel2, border: `1px solid ${C.line}` }}
        >
          <div className="flex justify-center flex-wrap gap-2.5" data-testid="pin-dots">
            {Array.from({ length }).map((_, i) => (
              <div
                key={i}
                className="w-3.5 h-3.5 rounded-full transition-colors duration-150"
                style={
                  i < value.length
                    ? { background: C.amber, border: `1px solid ${C.amber}` }
                    : { background: "transparent", border: `1px solid ${C.line}` }
                }
              />
            ))}
          </div>
        </div>

        {error && (
          <p className="text-center text-sm mb-4 px-2" style={{ color: "#d97757" }} data-testid="login-error">
            {error}
          </p>
        )}

        <NumericKeypad
          onDigit={press}
          onBackspace={backspace}
          onSubmit={handleOk}
          showSubmit
          backspaceSubLabel="Retour"
          submitSubLabel="Entrée"
          disabled={loading}
          testIdPrefix="pin-pad"
          keyClassName="min-h-[68px] rounded-xl text-xl font-medium font-mono transition-colors duration-100 active:scale-95 transition-transform disabled:opacity-50 bg-[#20252d] border border-[#2b313b] text-[#f0ede4] hover:border-[#e8a33d]"
        />

        <div className="text-center mt-4 space-y-2">
          <p className="text-xs" style={{ color: C.muted }}>
            Le clavier physique fonctionne aussi : chiffres,{" "}
            <kbd className="font-mono px-1.5 py-0.5 rounded text-[11px]" style={{ background: C.panel2, border: `1px solid ${C.line}` }}>⌫</kbd>
            {", "}
            <kbd className="font-mono px-1.5 py-0.5 rounded text-[11px]" style={{ background: C.panel2, border: `1px solid ${C.line}` }}>↵</kbd>
          </p>
          <p className="text-xs" style={{ color: C.muted }} data-testid="login-badge-hint">
            Vous pouvez aussi passer votre badge à tout moment.
          </p>
          {nfcDisponible && (
            <button
              onClick={scannerViaNfc}
              disabled={loading || nfcScanning}
              data-testid="login-nfc-scan-button"
              className="flex items-center gap-1.5 text-sm mx-auto hover:opacity-70 transition-opacity"
              style={{ color: C.muted }}
            >
              {nfcScanning ? <Loader2 size={14} className="animate-spin" /> : <Nfc size={14} />}
              {nfcScanning ? "Approchez le badge..." : "Scanner un badge NFC"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
